import type * as api from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';

import { useNavigation } from '../../../navigation';
import { apiClient, type SiteCapability } from '../../../state';
import { useSiteAudienceCommand } from '../../AdaptiveContent';
import { getPaletteLocationOptions } from './getPaletteLocationOptions';
import { getPaletteSpaceOptions } from './getPaletteSpaceOptions';
import { useDeleteSiteItems } from './useDeleteSiteItems';
import { useSetDefaultSiteVariant } from './useSetDefaultSiteVariant';

export interface SiteVariantActionsButtonProps extends Pick<ButtonProps, 'size' | 'disabled'> {
    siteSpace: api.SiteSpace;
    site: api.Site;
    organization: api.Organization;
    sitePermissions: SiteCapability;
    siteStructure: api.SiteStructure;
}

/**
 * Action button to open actions palette for a site variant.
 */
export function SiteVariantActionsButton(props: SiteVariantActionsButtonProps) {
    const { siteSpace, site, organization, sitePermissions, siteStructure, ...buttonProps } = props;
    const organizationId = organization.id;
    const deleteSiteItems = useDeleteSiteItems({ organization, site });
    const setDefaultVariant = useSetDefaultSiteVariant({ organization, site, siteStructure });

    const { navigate } = useNavigation();
    const siteAudience = useSiteAudienceCommand({
        organizationId,
        site,
        target: 'variant',
        condition: siteSpace.condition ? siteSpace.condition : undefined,
        onChange: async (condition) => {
            await apiClient.siteSpaces.updateSiteSpaceById(organizationId, site.id, siteSpace.id, {
                condition: condition ?? null,
            });
        },
    });

    return (
        <>
            {siteAudience?.dialogs}

            <PaletteButton
                palette={() =>
                    createPalette({
                        groups: [
                            getPaletteSpaceOptions({ space: siteSpace.space, navigate }),
                            siteSpace.urls.published
                                ? getPaletteLocationOptions(siteSpace.urls.published)
                                : null,
                            sitePermissions.canAdminSite
                                ? {
                                      key: 'update-options',
                                      commands: [
                                          {
                                              key: 'siteSpace.setDefault',
                                              title: 'Set as home',
                                              icon: Icon.Home,
                                              disabled: Boolean(siteSpace.default),
                                              subtitle: siteSpace.default
                                                  ? 'Already set as home'
                                                  : undefined,
                                              trigger: () => {
                                                  setDefaultVariant(siteSpace);
                                              },
                                          },
                                          siteAudience?.command ?? null,
                                          {
                                              key: 'siteSpace.delete',
                                              title: 'Remove',
                                              icon: Icon.Trashcan,
                                              kind: 'danger',
                                              trigger: () => {
                                                  deleteSiteItems([siteSpace]);
                                              },
                                          },
                                      ].filter((x) => x !== null),
                                  }
                                : null,
                        ].filter((x) => x !== null),
                    })
                }
                kind="nested"
                label="Actions"
                leadingIcon={Icon.MoreVertical}
                iconOnly
                {...buttonProps}
            />
        </>
    );
}
